import { useEffect, useState, useCallback, useRef } from 'react';

import { captureExceptionWithTags } from 'src/utils/sentry';

// This will be passed to the sentry capture function to indicate the type of error view
type ErrorViewType = 'error-section' | 'error-flag';

// This hook returns a util function that handles checking the Sentry related feature flag and
// capturing exceptions in Sentry. Regardless of the flag state it will capture the event in Sentry.
// If flag is enabled, it returns the Sentry ID, otherwise it returns an empty string.
export const useCaptureSentryException = ({
  errorViewType,
}: {
  errorViewType: ErrorViewType;
}) => {
  const captureSentryException = useCallback(
    async (errorToCapture: Error): Promise<string> => {
      const sentryId = await captureExceptionWithTags(errorToCapture, {
        visibleToUser: 'true',
        viewType: errorViewType,
      });

      return sentryId || '';
    },
    [errorViewType]
  );

  return { captureSentryException };
};

// This hook uses the `useCaptureSentryException` hook to capture an exception
// automatically and handles storing the Sentry ID and returning it.
export const useSentryExceptionId = ({
  error,
  errorViewType,
}: {
  error?: Error;
  errorViewType: ErrorViewType;
}): string => {
  const [sentryId, setSentryId] = useState<string>('');
  const { captureSentryException } = useCaptureSentryException({
    errorViewType,
  });

  // used to track the last error so we don't capture the same one multiple times
  const lastErrorRef = useRef<Error | undefined>(undefined);

  useEffect(() => {
    (async () => {
      if (error && error !== lastErrorRef.current) {
        // Update the ref to the current error
        lastErrorRef.current = error;
        const id = await captureSentryException(error);
        setSentryId(id);
      }
    })();
  }, [error, captureSentryException]);

  return sentryId;
};
